import { useQuery } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, queryClient } from '../lib/api';
import { formatRelative } from '../lib/format';
import { Button, clsx } from './ui';

/**
 * New notifications arrive over a server-sent event stream the moment they
 * are written (a shop order reaching a supplier, say); the list is also
 * polled every few minutes in case the stream is down. The list opens in a
 * small panel.
 */
export function NotificationBell({ dark }: { dark?: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState(false);
  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications.list({ limit: 20 }),
    // the stream is the fast path; polling is the fallback (faster while the stream is down)
    refetchInterval: () => (live ? 5 * 60_000 : 60_000),
  });
  const unread = list.data?.unreadCount ?? 0;

  // Only a visible tab keeps the stream open: browsers allow few connections per site
  // over HTTP/1.1, so background tabs mustn't hold them; a tab catches up when shown again
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    const refetch = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
    const open = () => {
      if (es || document.visibilityState === 'hidden') return;
      es = new EventSource(api.notifications.streamUrl(), { withCredentials: true });
      es.addEventListener('ready', () => setLive(true));
      es.addEventListener('notification', refetch);
      // EventSource reconnects by itself; poll meanwhile
      es.onerror = () => setLive(false);
    };
    const close = () => {
      es?.close();
      es = null;
      setLive(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') close();
      else {
        open();
        refetch();
      }
    };
    open();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function openItem(id: string, data: Record<string, unknown> | null, readAt: string | null) {
    if (!readAt) {
      await api.notifications.markRead([id]);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    setOpen(false);
    // a delivery is acknowledged on the transfer, even when it was sent for a shop order
    if (data?.transferId) navigate(`/transfers/${data.transferId}`);
    else if (data?.branchOrderId) navigate(`/shop-orders/${data.branchOrderId}`);
    else if (data?.adjustmentId) navigate(`/adjustments/${data.adjustmentId}`);
    else if (data?.shipmentId) navigate(`/shipments/${data.shipmentId}`);
    else if (data?.orderId) navigate(`/orders/${data.orderId}`);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={clsx('relative rounded-md p-1.5', dark ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-ink-soft hover:bg-ink/5 hover:text-ink')}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className={clsx('absolute z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface text-ink shadow-xl', dark ? 'left-0' : 'right-0')}>
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await api.notifications.markAllRead();
                  queryClient.invalidateQueries({ queryKey: ['notifications'] });
                }}
              >
                Mark all read
              </Button>
            )}
          </div>
          <ul className="max-h-96 divide-y divide-line overflow-y-auto">
            {list.data?.data.length === 0 && <li className="px-3 py-6 text-center text-sm text-ink-soft">Nothing yet.</li>}
            {list.data?.data.map((n) => (
              <li key={n.id}>
                <button onClick={() => openItem(n.id, n.data, n.readAt)} className={clsx('block w-full px-3 py-2 text-left hover:bg-paper', !n.readAt && 'bg-teal-wash/40')}>
                  <div className={clsx('text-sm', !n.readAt && 'font-medium')}>{n.title}</div>
                  {n.body && <div className="truncate text-xs text-ink-soft">{n.body}</div>}
                  <div className="text-xs text-ink-faint">{formatRelative(n.createdAt)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
